/**
 * Linha — agrupa campos com espaçamento e alinhamento próprios.
 *
 * Continua sendo uma grade de 24, não um flex. Trocar por flex pareceria natural
 * ("é uma linha"), mas perderia a coluna de 24 — e é justamente a grade que faz
 * dois campos de `colSpan: 12` ficarem lado a lado. A `row` acrescenta controle
 * de `gap`, `align` e `justify`, nada mais.
 */
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup } from '@angular/forms';

import { ScreenNode } from '../../model/node-props';
import { trackByNodeId } from '../../model/screen-index';
import { TranslateService } from '@ngx-translate/core';
import { translateTAValue } from '../../../system-utils/pipes/translateTA';
import { NodeHostComponent } from '../node-host.component';
import { NodeRenderer, RenderMode } from '../node-renderer';
import { childLayoutStyles, safeRawStyles, styleTokenClasses } from './child-layout';

@Component({
  selector: 'tem-row-container',
  standalone: true,
  imports: [CommonModule, NodeHostComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="tem-row"
      [class.tem-row--vazio]="isEmpty()"
      [attr.data-vazio]="isEmpty() ? emptyLabel() : null"
      [ngClass]="rowClasses()"
      [ngStyle]="rawStyles()">
      @for (child of children(); track trackBy(0, child)) {
        <div [ngStyle]="layoutOf(child)">
          <tem-node-host [node]="child" [form]="form()" [mode]="mode()" />
        </div>
      }
    </div>
  `,
  styleUrl: './row.renderer.scss'
})
export class RowContainerRenderer implements NodeRenderer<'row'> {

  readonly node = input.required<ScreenNode<'row'>>();
  readonly form = input<FormGroup | null>(null);
  readonly mode = input<RenderMode>('view');

  readonly trackBy = trackByNodeId;

  readonly children = computed<readonly ScreenNode[]>(() => this.node().children ?? []);

  /**
   * Vazia — e por isso invisivel sem a marca.
   *
   * Uma grade sem itens ocupa altura zero. A marca so vale no editor: em
   * producao um container vazio nao deve empurrar a tela para baixo.
   */
  readonly isEmpty = computed<boolean>(() => this.children().length === 0);

  readonly emptyLabel = computed<string>(
    () => translateTAValue(this.translate, 'builderScreen.container.empty')
  );

  private readonly translate = inject(TranslateService);

  readonly rowClasses = computed<Readonly<Record<string, boolean>>>(() => {
    const props = this.node().props;
    const classes: Record<string, boolean> = { ...styleTokenClasses(this.node().style) };

    if (props.gap !== undefined) {
      classes['tem-row--gap-' + props.gap] = true;
    }
    if (props.alignItems !== undefined) {
      classes['tem-row--align-' + props.alignItems] = true;
    }
    if (props.justifyContent !== undefined) {
      classes['tem-row--justify-' + props.justifyContent] = true;
    }

    return classes;
  });

  readonly rawStyles = computed(() => safeRawStyles(this.node().style));

  layoutOf(child: ScreenNode): Readonly<Record<string, string>> {
    return childLayoutStyles(child);
  }
}
